import React, { memo, useCallback, useRef } from 'react';
import { identity } from 'ramda';
import uuid from 'uuid-random';
import { useTranslations } from 'js/hooks/i18n';
import { I18n } from 'js/components/common/I18n';
import { Icon } from 'js/components/common/Icon';
import { clsx, clsb } from 'js/utils/classNames.util';
import { componentService } from 'js/services/component.service';
import { AccessibilityElement } from 'js/components/common/AccessibilityElement';

import * as Types from './types';
import { Label } from '../Label';
import * as helpers from '../../helpers';
import { withFinalFormField } from '../../hocs';

import '../../FormFields.scss';
import style from './Radio.scss';

export const RadioBare: React.FC<Types.IRadioProps> = ({
    id,
    meta,
    label,
    checked,
    onChange,
    onKeyDown,
    classNames,
    infoTooltip,
    className = '',
    disabled = false,
    readOnly = false,
    ...props
}) => {
    const idRef = useRef<string>(id || uuid());
    const translate = useTranslations('playtech.mobile.system.radio.');
    const radioRef = useRef<HTMLInputElement | null>(null);
    const isDisabled = disabled || readOnly || meta?.submitting;
    const titleAriaLabel = translate('aria-label.title');
    const stateAriaLabel = checked
        ? translate('aria-label.checked')
        : translate('aria-label.unchecked');

    const labelContent = typeof label === 'string'
        ? <I18n tKey={label} defaultValue={label} />
        : label;

    const interactionClass = helpers.getInteractionClass({
        readOnly,
        error: meta?.error,
        disabled: isDisabled,
        active: meta?.active,
        touched: meta?.touched,
        submitting: meta?.submitting
    });

    const handleOnKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>): void => {
            const { key } = event;

            onKeyDown?.(event);

            if (key === ' ' || key === 'Spacebar') {
                event.preventDefault();

                radioRef.current?.click();
            }
        },
        [radioRef, onKeyDown]
    );

    return (
        <div className={clsx([clsb([style, classNames], ['container']), interactionClass])}>
            <AccessibilityElement
                tagName="label"
                htmlFor={idRef.current}
                onKeyDown={handleOnKeyDown}
                tabIndex={isDisabled || readOnly ? -1 : 0}
                aria-label={`${titleAriaLabel} ${props['aria-label'] || ''} ${stateAriaLabel}`}
                className={clsx([clsb([style, classNames], ['containerInner']), className])}
            >
                <input
                    type="radio"
                    tabIndex={-1}
                    ref={radioRef}
                    id={idRef.current}
                    readOnly={readOnly}
                    disabled={isDisabled}
                    // this line is needed for using this component as replacer
                    {...checked === undefined ? {} : { checked }}
                    onChange={readOnly ? identity : onChange}
                    className={clsb([style, classNames], ['input'])}
                    {...props}
                />

                <Icon disabled={isDisabled || readOnly} type="radioChecked" className={clsx([clsb([style, classNames], ['radio']), style.radioChecked])} />
                <Icon disabled={isDisabled || readOnly} type="radioUnchecked" className={clsx([clsb([style, classNames], ['radio']), style.radioUnchecked])} />

                {label && (
                    <Label
                        label={labelContent}
                        id={idRef.current}
                        classNames={classNames}
                        infoTooltip={infoTooltip}
                        className={clsx([clsb([style, classNames], ['label'])])}
                    />
                )}

            </AccessibilityElement>
        </div>
    );
};

export const RadioPure = memo(RadioBare);
export const Radio = withFinalFormField<string>({ type: 'radio' })(RadioPure);

componentService.registerWebContentComponents({ Radio: RadioPure });
